/**
 * Frontend domain — primitives. Split out of ComponentDomain.ts on 2026-08-15.
 *
 * ComponentDomain.ts remains the PUBLIC FACADE its own title always claimed, re-exporting
 * every one of these; nothing that imports it needs to change. New code should import the
 * narrow module instead: of the 58 files that consume this barrel, 34 touch exactly ONE of
 * these four areas, so most of them were depending on four times what they used.
 *
 * Mostly types, plus the enums and the one constant below, which do emit. (This header read
 * "Types only — no runtime cost either way" until 2026-09-03; the enums have always compiled to
 * real objects, so that was never true — see §28.88 on comments asserting mechanisms.) The value
 * is that a reader can see what a consumer actually needs, and that the four areas can now move
 * independently.
 */
// -----------------------------------------------------------------------------
// SECTION 1: PRIMITIVE VALUE TYPES & ENUMS
// -----------------------------------------------------------------------------

/**
 * Lives a player starts a game with — the frontend's single spelling of the number.
 *
 * The BACKEND owns this rule (`PlayerLives.INITIAL_LIVES`, which validates and defaults against
 * it); this is the client's copy of a value it cannot import across the workspace boundary, and
 * the two must agree. It exists because the literal `6` was written in four places here —
 * playerLivesSlice's two, and ChallengerSelection's two — none of which could see each other.
 *
 * Every use is a FALLBACK for "the lives map has no entry for this player yet", which is why the
 * server's real value always wins when one has arrived.
 */
export const INITIAL_LIVES = 6;

export type PlayerId = string;
export type RoomId = string;
export type GameId = string;

export enum GamePhase {
  IDLE = 'IDLE',
  LOBBY = 'LOBBY',
  STARTING = 'STARTING',
  ROLLING = 'ROLLING',
  PEEKING = 'PEEKING',
  CLAIMING = 'CLAIMING',
  DECIDING = 'DECIDING',
  REVEALING = 'REVEALING',
  ROUND_END = 'ROUND_END',
  GAME_OVER = 'GAME_OVER',
}

export enum PlayerStatus {
  WAITING = 'WAITING',
  ACTIVE = 'ACTIVE',
  SPECTATING = 'SPECTATING',
  DISCONNECTED = 'DISCONNECTED',
  ELIMINATED = 'ELIMINATED',
}

export enum MonetizationTrack {
  FREE = 'FREE',
  PREMIUM = 'PREMIUM',
}

export enum CupAnimationState {
  STACKED = 'STACKED',
  SHAKING = 'SHAKING',
  RESTING = 'RESTING',
  PEEKING = 'PEEKING',
  LIFTING = 'LIFTING',
  REVEALED = 'REVEALED',
}


export enum RoundOutcome {
  BLUFF_CAUGHT = 'BLUFF_CAUGHT',
  TRUTH_REVEALED = 'TRUTH_REVEALED',
  MEYER_CALLED = 'MEYER_CALLED',
}

// -----------------------------------------------------------------------------
